import { useState } from 'react'
import { ImagePlus, Plus, X } from 'lucide-react'
import type { ReportTheme, ReportThemePatch, ThemeBlock, FontFamily } from '../../types/reportTheme'
import { Toggle, ColorField, SelectField } from '../reports/config/controls'
import { ConfigSection } from '../reports/config/ConfigSection'
import { uploadThemeLogo } from './reportThemesApi'
import { derivePalette, PALETTE_PRESETS } from '../reports/chartPalette'

interface ThemeEditorProps {
  theme: ReportTheme
  onChange: (patch: ReportThemePatch) => void
}

const INPUT_CLASS =
  'w-full rounded-md border border-[#E2E4ED] bg-white px-2 py-1.5 text-sm text-[#1B2559] focus:border-[#6366F1] focus:outline-none'
const LABEL_CLASS = 'text-xs text-[#8B8FA7]'

const FONT_OPTIONS = [
  { value: 'sans', label: 'Sans' },
  { value: 'serif', label: 'Serif' },
  { value: 'mono', label: 'Mono' },
]

/**
 * Éditeur d'un thème de rapport. Composant CONTRÔLÉ : aucune valeur en état local,
 * chaque modification remonte au parent via onChange(patch partiel). La persistance
 * (updateTheme) est gérée par le parent (page bibliothèque).
 */
export function ThemeEditor({ theme, onChange }: ThemeEditorProps) {
  function updateBlock(key: 'cover' | 'header' | 'footer' | 'contact' | 'intro', partial: Record<string, unknown>) {
    const current = theme[key] as ThemeBlock
    onChange({ [key]: { ...current, ...partial } } as ReportThemePatch)
  }

  async function handleLogo(field: 'clientLogoUrl' | 'agencyLogoUrl', file: File | undefined) {
    if (!file) return
    try {
      const url = await uploadThemeLogo(file)
      onChange({ [field]: url } as ReportThemePatch)
    } catch {
      // Upload échoué : le thème est conservé sans logo, on ne casse pas l'éditeur.
    }
  }

  async function handleCoverBg(file: File | undefined) {
    if (!file) return
    try {
      const url = await uploadThemeLogo(file)
      updateBlock('cover', { backgroundImageUrl: url })
    } catch {
      // Upload échoué : couverture conservée sans image de fond.
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <ConfigSection title="Marque">
        <label htmlFor="theme-name" className="flex flex-col gap-1">
          <span className={LABEL_CLASS}>Nom du thème</span>
          <input
            id="theme-name"
            type="text"
            value={theme.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className={INPUT_CLASS}
          />
        </label>
        <ColorField
          label="Couleur primaire"
          value={theme.primaryColor}
          onChange={(v) => onChange({ primaryColor: v ?? '#6366F1' })}
        />
        <ColorField
          label="Couleur secondaire"
          value={theme.secondaryColor}
          onChange={(v) => onChange({ secondaryColor: v ?? '#1B2559' })}
        />
        <SelectField
          label="Police"
          value={theme.fontFamily}
          options={FONT_OPTIONS}
          onChange={(v) => onChange({ fontFamily: v as FontFamily })}
        />
      </ConfigSection>

      <ConfigSection title="Palette de séries">
        <p className={LABEL_CLASS}>
          Couleurs utilisées pour les séries des graphiques (indépendantes de l’habillage).
        </p>
        <button
          type="button"
          onClick={() => onChange({ chartPalette: derivePalette(theme.primaryColor, theme.secondaryColor, 10) })}
          className="rounded-md border border-[#E2E4ED] bg-white px-3 py-1.5 text-xs font-medium text-[#6366F1] hover:bg-[#F4F5FA]"
        >
          Générer depuis mes couleurs de marque
        </button>
        <SelectField
          label="Preset de palette"
          value=""
          options={[{ value: '', label: 'Personnalisée' }, ...PALETTE_PRESETS.map((p) => ({ value: p.id, label: p.label }))]}
          onChange={(v) => {
            const preset = PALETTE_PRESETS.find((p) => p.id === v)
            if (preset) onChange({ chartPalette: preset.colors })
          }}
        />
        {theme.chartPalette.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {theme.chartPalette.map((color, i) => (
              <div key={i} className="flex items-center gap-1 rounded-md border border-[#E2E4ED] bg-[#F8F9FC] p-1">
                <input
                  type="color"
                  aria-label={`Couleur de série ${i + 1}`}
                  value={color}
                  onChange={(e) => {
                    const next = [...theme.chartPalette]
                    next[i] = e.target.value
                    onChange({ chartPalette: next })
                  }}
                  className="h-6 w-8 cursor-pointer rounded border border-[#E2E4ED] bg-white p-0.5"
                />
                <button
                  type="button"
                  aria-label={`Retirer la couleur de série ${i + 1}`}
                  onClick={() => onChange({ chartPalette: theme.chartPalette.filter((_, k) => k !== i) })}
                  className="flex h-5 w-5 items-center justify-center rounded text-[#8B8FA7] hover:bg-white hover:text-[#EF4444]"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => onChange({ chartPalette: [...theme.chartPalette, '#6366F1'] })}
          className="flex w-fit items-center gap-1 rounded-md border border-dashed border-[#C8CBD8] px-2 py-1 text-xs font-medium text-[#8B8FA7] hover:border-[#6366F1] hover:text-[#6366F1]"
        >
          <Plus size={12} /> Ajouter une couleur
        </button>
      </ConfigSection>

      <ConfigSection title="Logos">
        <LogoUploadField
          id="logo-client"
          label="Logo client"
          url={theme.clientLogoUrl}
          onFile={(f) => handleLogo('clientLogoUrl', f)}
          onRemove={() => onChange({ clientLogoUrl: null })}
        />
        <LogoUploadField
          id="logo-agency"
          label="Logo agence"
          url={theme.agencyLogoUrl}
          onFile={(f) => handleLogo('agencyLogoUrl', f)}
          onRemove={() => onChange({ agencyLogoUrl: null })}
        />
      </ConfigSection>

      <ConfigSection title="Page de garde">
        <Toggle
          label="Activer la page de garde"
          checked={!!theme.cover.enabled}
          onChange={(v) => updateBlock('cover', { enabled: v })}
        />
        <label htmlFor="cover-title" className="flex flex-col gap-1">
          <span className={LABEL_CLASS}>Titre de couverture</span>
          <input
            id="cover-title"
            type="text"
            value={(theme.cover.title as string) ?? ''}
            onChange={(e) => updateBlock('cover', { title: e.target.value })}
            className={INPUT_CLASS}
          />
        </label>
        <label htmlFor="cover-signature" className="flex flex-col gap-1">
          <span className={LABEL_CLASS}>Préparé par (signature)</span>
          <input
            id="cover-signature"
            type="text"
            value={(theme.cover.signature as string) ?? ''}
            onChange={(e) => updateBlock('cover', { signature: e.target.value })}
            placeholder="Ex : Elorion"
            className={INPUT_CLASS}
          />
        </label>
        <LogoUploadField
          id="cover-bg"
          label="Image de fond"
          url={(theme.cover.backgroundImageUrl as string) ?? null}
          onFile={handleCoverBg}
          onRemove={() => updateBlock('cover', { backgroundImageUrl: null })}
        />

        <div className="mt-1 flex flex-col gap-3 border-t border-[#EEF0F6] pt-3">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[#8B8FA7]">Habillage</span>
          <Toggle
            label="Cartouche derrière logos/textes"
            checked={!!theme.cover.cartouche}
            onChange={(v) => updateBlock('cover', { cartouche: v })}
          />
          {theme.cover.cartouche ? (
            <>
              <ColorField
                label="Couleur du cartouche"
                value={(theme.cover.cartoucheColor as string) ?? '#0C0F1A'}
                onChange={(v) => updateBlock('cover', { cartoucheColor: v ?? '#0C0F1A' })}
              />
              <label htmlFor="cover-cartouche-opacity" className="flex flex-col gap-1">
                <span className={LABEL_CLASS}>Opacité du cartouche · {Number((theme.cover.cartoucheOpacity as number) ?? 40)}%</span>
                <input
                  id="cover-cartouche-opacity"
                  type="range"
                  min={0}
                  max={100}
                  value={Number((theme.cover.cartoucheOpacity as number) ?? 40)}
                  onChange={(e) => updateBlock('cover', { cartoucheOpacity: Number(e.target.value) })}
                  className="accent-[#6366F1]"
                />
              </label>
            </>
          ) : null}
          <label htmlFor="cover-radius" className="flex flex-col gap-1">
            <span className={LABEL_CLASS}>Arrondi des angles (px)</span>
            <input
              id="cover-radius"
              type="number"
              min={0}
              max={48}
              value={Number((theme.cover.radius as number) ?? 8)}
              onChange={(e) => updateBlock('cover', { radius: Number(e.target.value) })}
              className={INPUT_CLASS}
            />
          </label>
          <Toggle
            label="Bandeau de pied de couverture"
            checked={!!theme.cover.footerBand}
            onChange={(v) => updateBlock('cover', { footerBand: v })}
          />
        </div>
      </ConfigSection>

      <ConfigSection title="En-tête">
        <Toggle
          label="Activer l’en-tête"
          checked={!!theme.header.enabled}
          onChange={(v) => updateBlock('header', { enabled: v })}
        />
      </ConfigSection>

      <ConfigSection title="Pied de page">
        <Toggle
          label="Activer le pied de page"
          checked={!!theme.footer.enabled}
          onChange={(v) => updateBlock('footer', { enabled: v })}
        />
        <label htmlFor="footer-mentions" className="flex flex-col gap-1">
          <span className={LABEL_CLASS}>Mentions</span>
          <input
            id="footer-mentions"
            type="text"
            value={(theme.footer.mentions as string) ?? ''}
            onChange={(e) => updateBlock('footer', { mentions: e.target.value })}
            placeholder="Ex : Document confidentiel"
            className={INPUT_CLASS}
          />
        </label>
      </ConfigSection>

      <ConfigSection title="Contact">
        <Toggle
          label="Activer le bloc contact"
          checked={!!theme.contact.enabled}
          onChange={(v) => updateBlock('contact', { enabled: v })}
        />
        <label htmlFor="contact-name" className="flex flex-col gap-1">
          <span className={LABEL_CLASS}>Nom du contact</span>
          <input
            id="contact-name"
            type="text"
            value={(theme.contact.name as string) ?? ''}
            onChange={(e) => updateBlock('contact', { name: e.target.value })}
            className={INPUT_CLASS}
          />
        </label>
        <label htmlFor="contact-email" className="flex flex-col gap-1">
          <span className={LABEL_CLASS}>Email du contact</span>
          <input
            id="contact-email"
            type="email"
            value={(theme.contact.email as string) ?? ''}
            onChange={(e) => updateBlock('contact', { email: e.target.value })}
            className={INPUT_CLASS}
          />
        </label>
      </ConfigSection>

      <ConfigSection title="Introduction">
        <Toggle
          label="Activer l’introduction"
          checked={!!theme.intro.enabled}
          onChange={(v) => updateBlock('intro', { enabled: v })}
        />
        <label htmlFor="intro-text" className="flex flex-col gap-1">
          <span className={LABEL_CLASS}>Texte d’introduction</span>
          <textarea
            id="intro-text"
            value={(theme.intro.text as string) ?? ''}
            onChange={(e) => updateBlock('intro', { text: e.target.value })}
            rows={3}
            className={INPUT_CLASS}
          />
        </label>
      </ConfigSection>
    </div>
  )
}

interface LogoUploadFieldProps {
  id: string
  label: string
  url: string | null
  onFile: (file: File | undefined) => void
  onRemove: () => void
}

/**
 * Champ d'upload de logo stylé. L'input natif est masqué (sr-only) mais reste
 * accessible : `aria-label={label}` porte le nom accessible, la zone cliquable est
 * le <label> qui l'enveloppe.
 */
function LogoUploadField({ id, label, url, onFile, onRemove }: LogoUploadFieldProps) {
  const [key, setKey] = useState(0)
  return (
    <div className="flex flex-col gap-1.5">
      <span className={LABEL_CLASS}>{label}</span>
      {url ? (
        <div className="flex items-center gap-2 rounded-lg border border-[#E2E4ED] bg-[#F8F9FC] p-2">
          <img src={url} alt={`Aperçu ${label.toLowerCase()}`} className="h-9 w-auto max-w-[120px] object-contain" />
          <div className="ml-auto flex items-center gap-1">
            <label
              htmlFor={id}
              className="cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium text-[#6366F1] hover:bg-white"
            >
              Remplacer
            </label>
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Retirer le ${label.toLowerCase()}`}
              className="flex h-6 w-6 items-center justify-center rounded-md text-[#8B8FA7] hover:bg-white hover:text-[#EF4444]"
            >
              <X size={13} />
            </button>
          </div>
        </div>
      ) : (
        <label
          htmlFor={id}
          className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-[#C8CBD8] bg-[#F8F9FC] px-3 py-3 text-xs font-medium text-[#8B8FA7] transition-colors hover:border-[#6366F1] hover:text-[#6366F1]"
        >
          <ImagePlus size={16} strokeWidth={1.75} />
          Importer une image
        </label>
      )}
      <input
        key={key}
        id={id}
        type="file"
        accept="image/*"
        aria-label={label}
        className="sr-only"
        onChange={(e) => {
          onFile(e.target.files?.[0])
          setKey((k) => k + 1)
        }}
      />
    </div>
  )
}
